import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { finalize, map, Observable, shareReplay } from 'rxjs';

import {
  StepUpMfaDialogComponent,
  StepUpMfaDialogData
} from './step-up-mfa-dialog.component';

/**
 * StepUpService (LION-520) — orquesta el step-up MFA de forma TRANSVERSAL.
 *
 * `ensureStepUp()` abre el modal canónico (StepUpMfaDialogComponent), que llama a
 * AuthService.stepUpMfa() y persiste el accessToken reemitido. Resuelve `true` si
 * el segundo factor se revalidó, `false` si el usuario canceló o falló.
 *
 * SERIALIZACIÓN (anti-race de modales): replica EXACTAMENTE el patrón
 * `_refreshInFlight$` de AuthService.refreshToken() — observable en vuelo
 * compartido + shareReplay(1) + finalize que lo nulifica. Si N requests reciben
 * 403 MFA_STEPUP_REQUIRED casi simultáneamente, TODAS comparten el MISMO modal y,
 * al resolver, todas reintentan con el token nuevo. MatDialog por sí solo NO
 * previene instancias múltiples: esta caché es la que garantiza un único modal.
 */
@Injectable({ providedIn: 'root' })
export class StepUpService {
  private readonly _dialog = inject(MatDialog);

  // Step-up en vuelo compartido. Mismo contrato que _refreshInFlight$.
  private _stepUpInFlight$: Observable<boolean> | null = null;

  /**
   * Garantiza un step-up MFA fresco. Si ya hay uno en vuelo, devuelve el MISMO
   * observable (un solo modal); si no, abre el modal y cachea el flujo.
   *
   * `actionLabel` es una etiqueta de UI OPCIONAL ("Resetear la contraseña de
   * Juan Pérez") que el call-site publica en el HttpContext y el interceptor
   * reenvía acá: le explica al usuario por qué se le pide el segundo factor.
   * Nunca es un id ni un secreto, y sin ella el modal se comporta igual que hoy.
   *
   * OJO — la serialización GANA sobre la etiqueta: si ya hay un step-up en
   * vuelo, el modal se reusa con la etiqueta de la PRIMERA request y la de las
   * siguientes se descarta. Es correcto y no hay que "arreglarlo": el usuario
   * está confirmando UN segundo factor, no N acciones; ese único step-up
   * desbloquea a todas las requests que lo esperaban.
   */
  ensureStepUp(actionLabel?: string | null): Observable<boolean> {
    if (this._stepUpInFlight$) {
      return this._stepUpInFlight$;
    }

    this._stepUpInFlight$ = this._dialog
      .open<StepUpMfaDialogComponent, StepUpMfaDialogData, boolean>(
        StepUpMfaDialogComponent,
        {
          autoFocus: false,
          disableClose: false,
          // 452px es el ancho del diseño; el maxWidth deja que abajo de 600px
          // la hoja ocupe prácticamente todo el viewport.
          width: '452px',
          maxWidth: '96vw',
          // Layout responsive del panel (definido en src/styles/styles.scss): el
          // panel se monta fuera del ViewEncapsulation del componente. Abajo de
          // 600px pisa width/maxWidth con !important y lo vuelve full-screen.
          panelClass: 'mfa-dialog-panel',
          data: { actionLabel: actionLabel ?? null }
        }
      )
      .afterClosed()
      .pipe(
        // afterClosed() emite el valor del close() (true/false) o undefined si se
        // cierra por backdrop/ESC: normalizamos a boolean.
        map(result => result === true),
        finalize(() => {
          this._stepUpInFlight$ = null;
        }),
        shareReplay(1)
      );

    return this._stepUpInFlight$;
  }
}
